// 04.2 · Agenda del día — selector de 7 días, chips, partidos por franja
// (Tarde / Noche), cobertura del motor y campana de recordatorio.
// Fuente: RTDB /today_games/{fecha} (prematch_list.py). Sin cobertura =
// is_bl / categoria no_recomendada: se lista pero no puede generar señal.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { santiagoDateStr, shiftDays } from '../../../components/PerformanceLab';
import { Nav, EmptyInline, SigSkeleton, I } from '../ui';
import { useTodayGames, useReminders, useOnline } from '../hooks';
import { OffBar } from '../ui';

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export default function Agenda() {
  const navigate = useNavigate();
  const hoy = santiagoDateStr(new Date());
  // ?fecha=YYYY-MM-DD: abrir un día concreto (deep link / QA)
  const qFecha = new URLSearchParams(window.location.search).get('fecha');
  const [fecha, setFecha] = useState(/^\d{4}-\d{2}-\d{2}$/.test(qFecha || '') ? qFecha : hoy);
  const [filtro, setFiltro] = useState('todos'); // todos | cobertura | basquet | handball
  const { games, loading, vacio } = useTodayGames(fecha);
  const { reminders, toggle } = useReminders();
  const { online, sinceStr } = useOnline();

  const dias = useMemo(() => [-1, 0, 1, 2, 3, 4, 5].map((o) => {
    const d = shiftDays(hoy, o);
    const dow = new Date(`${d}T12:00:00Z`).getUTCDay();
    return { d, label: o === 0 ? 'Hoy' : DIAS[dow], num: parseInt(d.slice(8, 10), 10) };
  }), [hoy]);

  const lista = games.filter((g) => {
    if (filtro === 'cobertura') return g.cobertura;
    if (filtro === 'basquet') return !g.esHandball;
    if (filtro === 'handball') return g.esHandball;
    return true;
  });
  const conCob = games.filter((g) => g.cobertura).length;
  const franja = (g) => (g.horaNum < 1200 ? 'Mañana' : g.horaNum < 1900 ? 'Tarde' : 'Noche');
  const grupos = ['Mañana', 'Tarde', 'Noche'].map((f) => ({ f, items: lista.filter((g) => franja(g) === f) })).filter((x) => x.items.length);
  const esHoy = fecha === hoy;

  return (
    <div className="v3"><div className="scroll nt safe-top">
      <Nav title="Agenda" onBack={() => navigate('/app/senales')} />
      {!online && <OffBar since={sinceStr} />}
      <div className="day-tabs">
        {dias.map((x) => (
          <button key={x.d} type="button" className={x.d === fecha ? 'on' : ''} onClick={() => setFecha(x.d)}>
            <span>{x.label}</span><b>{x.num}</b>
          </button>
        ))}
      </div>
      <div className="chips" style={{ paddingTop: 0 }}>
        <button type="button" className={`v3chip${filtro === 'todos' ? ' on' : ''}`} onClick={() => setFiltro('todos')}>Todos · {games.length}</button>
        <button type="button" className={`v3chip${filtro === 'cobertura' ? ' on' : ''}`} onClick={() => setFiltro('cobertura')}>Con cobertura · {conCob}</button>
        <button type="button" className={`v3chip${filtro === 'basquet' ? ' on' : ''}`} onClick={() => setFiltro('basquet')}>Básquet</button>
        <button type="button" className={`v3chip${filtro === 'handball' ? ' on' : ''}`} onClick={() => setFiltro('handball')}>Handball</button>
      </div>

      {loading && <><SigSkeleton /><SigSkeleton /></>}

      {!loading && (vacio || lista.length === 0) && (
        <EmptyInline icon={<I.cal width={20} height={20} stroke="#6E6E73" />}
          title={esHoy ? 'Sin agenda publicada para hoy' : 'Sin agenda para este día'}
          cta="Ver señales en vivo" ghost onCta={() => navigate('/app/senales')}>
          {esHoy
            ? 'La agenda del día se publica en la mañana con los partidos que el motor analizará. Mientras tanto, las señales en vivo siguen llegando.'
            : 'La agenda se publica el mismo día. Vuelve más tarde o revisa las señales en vivo.'}
        </EmptyInline>
      )}

      {!loading && grupos.map(({ f, items }) => (
        <div key={f}>
          <p className="grp" style={{ paddingTop: f === grupos[0].f ? 0 : 14 }}>{f}</p>
          {items.map((g) => {
            const on = !!reminders[g.gid];
            const vivo = g.estado === 'en_vivo';
            const fin = g.estado === 'finalizado';
            return (
              <div className="up" key={g.gid} style={fin ? { opacity: .55 } : undefined}>
                <div className="when"><b>{g.hora || '—'}</b><span>{vivo ? 'vivo' : fin ? 'final' : esHoy ? 'hoy' : ''}</span></div>
                <div className="b">
                  <p className="lg"><span className="lg">{g.liga || '—'}</span><span style={{ fontSize: 11 }}>{g.esHandball ? '🤾' : '🏀'}</span></p>
                  <p className="m">{g.home} vs {g.away}</p>
                </div>
                <span className={`cov${g.cobertura ? '' : ' n'}`} title={g.cobertura ? 'Cobertura del motor' : 'Sin cobertura'}><i /></span>
                <button type="button" className={`bellb${on ? ' on' : ''}`} aria-label="Recordatorio 10 min antes"
                  disabled={!g.cobertura || fin} onClick={() => toggle(g.gid, { hora: g.hora, partido: `${g.home} vs ${g.away}`, fecha })}>
                  <I.bell width={14} height={14} stroke={on ? '#FF4D0D' : '#6E6E73'} strokeWidth="2.2" />
                </button>
              </div>
            );
          })}
        </div>
      ))}

      {!loading && lista.length > 0 && (
        <p className="v3hint" style={{ textAlign: 'center', paddingTop: 12 }}>Punto azul = el motor analiza ese partido en vivo y puede emitir señal; gris = sin cobertura. La campana avisa 10 min antes del inicio.</p>
      )}
    </div></div>
  );
}
